import {
  Search, Target, Zap, Globe, Share2, TrendingUp, Layers, Settings, Video, Compass
} from 'lucide-react';

export const DIENSTEN = [
  {
    id: 'seo',
    route: '/seo',
    icon: Search,
    title: 'SEO',
    shortDesc: 'Duurzame groei via organische zoekresultaten. Bovenaan in Google zonder advertentiebudget.',
    metaTitle: 'SEO Bureau Zwolle — Meer Organisch Verkeer | IMPERIA AI',
    metaDescription: 'IMPERIA AI is het SEO bureau dat uw website naar de top van Google brengt. Technische SEO, linkbuilding en content strategie voor meetbare groei.',
    keywords: 'seo bureau, zoekmachine optimalisatie, seo zwolle, lokale seo, google optimalisatie',
    h1: 'SEO Bureau — Bovenaan in Google zonder advertentiebudget',
    intro: 'Organisch gevonden worden in Google is de meest kosteneffectieve manier om structureel nieuwe klanten aan te trekken. Wij zorgen dat uw website op de juiste zoekwoorden scoort — zodat potentiële klanten u vinden op het moment dat ze klaar zijn om te kopen.',
    problem: {
      title: 'Jouw concurrenten scoren bovenaan. Jij niet.',
      points: [
        'U investeert in uw website maar niemand vindt hem',
        'Je bent afhankelijk van dure Google Ads voor elk bezoek',
        'Je concurrenten pakken alle organische klanten af',
        'U weet niet waarom u niet rankt',
      ],
    },
    solution: {
      title: 'Wij brengen u structureel naar de top van Google',
      text: 'Met een bewezen SEO-aanpak optimaliseren we uw website technisch, bouwen we autoriteit op via linkbuilding en creëren we content die rankt. Het resultaat: meer organisch verkeer, meer leads en minder afhankelijkheid van betaalde advertenties.',
    },
    steps: [
      { n: 1, title: 'SEO Audit', desc: 'We analyseren uw huidige positie, technische SEO, zoekwoorden en concurrenten.' },
      { n: 2, title: 'Strategie op maat', desc: 'We stellen een duidelijk plan op met prioriteiten, zoekwoorden en een tijdlijn.' },
      { n: 3, title: 'Implementatie', desc: 'Technische optimalisaties, content verbetering en linkbuilding worden uitgevoerd.' },
      { n: 4, title: 'Groei & Rapportage', desc: 'Je ontvangt maandelijkse rapporten met rankings, verkeer en leads.' },
    ],
    benefits: [
      'Meer organisch verkeer zonder advertentiekosten',
      'Hogere Google rankings op relevante zoekwoorden',
      'Meer kwalitatieve leads via zoekmachines',
      'Duurzame groei die maanden en jaren blijft werken',
      'Onafhankelijkheid van advertentiebudgetten',
    ],
    services: [
      { name: 'SEO Setup', price: '€850' },
      { name: 'Technische SEO', price: '€650' },
      { name: 'On-Page SEO', price: '€550' },
      { name: 'Linkbuilding Startpakket', price: '€750' },
      { name: 'SEO Abonnement', price: 'v.a. €750/mnd' },
    ],
  },
  {
    id: 'google-ads',
    route: '/google-ads',
    icon: Target,
    title: 'Google Ads',
    shortDesc: 'Direct bovenaan in Google met betaalde advertenties. Meer leads, meetbare ROI.',
    metaTitle: 'Google Ads Bureau — Direct Meer Klanten | IMPERIA AI',
    metaDescription: 'Wij zijn het Google Ads bureau dat uw campagnes winstgevend maakt. Van setup tot optimalisatie — meer leads voor minder kosten.',
    keywords: 'google ads bureau, google ads specialist, ppc bureau, sea bureau, google advertenties',
    h1: 'Google Ads Bureau — Direct bovenaan in Google met meetbare ROI',
    intro: 'Google Ads is het krachtigste kanaal om snel nieuwe klanten te bereiken op het moment dat ze actief zoeken naar uw product of dienst. Wij zetten winstgevende campagnes op die converteren — en optimaliseren ze continu voor maximale ROI.',
    problem: {
      title: 'Google Ads zonder strategie = geld verbranden',
      points: [
        'Je betaalt voor klikken die nooit converteren',
        'Je campagnes zijn niet optimaal ingericht',
        'U weet niet welke zoekwoorden echt werken',
        'Je concurrenten pakken alle lucratieve posities',
      ],
    },
    solution: {
      title: 'Wij maken uw Google Ads winstgevend',
      text: 'Met grondige zoekwoordenanalyse, conversiegerichte advertentieteksten en slimme biedstrategieën zorgen wij dat elk euro die u investeert maximaal rendement oplevert. Geen verbrande budgetten meer.',
    },
    steps: [
      { n: 1, title: 'Account Analyse', desc: 'We analyseren uw huidige campagnes of starten met een grondige zoekwoordenanalyse.' },
      { n: 2, title: 'Campagne Setup', desc: 'Professionele inrichting van campagnestructuur, advertentiegroepen en biedstrategieën.' },
      { n: 3, title: 'Conversietracking', desc: 'Volledige tracking zodat we elke euro kunnen koppelen aan een resultaat.' },
      { n: 4, title: 'Continue Optimalisatie', desc: 'Wekelijkse optimalisaties op basis van data voor steeds betere resultaten.' },
    ],
    benefits: [
      'Direct zichtbaar bovenaan in Google',
      'Alleen betalen als iemand klikt',
      'Volledige controle over budget',
      'Meetbare ROI op elke euro',
      'Snelle resultaten — campagne live binnen 7 dagen',
    ],
    services: [
      { name: 'Google Ads Setup', price: '€650' },
      { name: 'Google Ads Audit', price: '€199' },
      { name: 'Google Ads Beheer', price: 'v.a. €750/mnd' },
    ],
  },
  {
    id: 'lead-generatie',
    route: '/lead-generatie',
    icon: Zap,
    title: 'Lead Generatie',
    shortDesc: 'Automatisch meer gekwalificeerde leads voor uw salesteam. Schaalbaar en meetbaar.',
    metaTitle: 'Lead Generatie Bureau — Meer Gekwalificeerde Leads | IMPERIA AI',
    metaDescription: 'Wij bouwen lead generatie systemen die automatisch gekwalificeerde leads aanleveren. Van funnel tot CRM — wij regelen het.',
    keywords: 'lead generatie bureau, leadmachine, b2b leads genereren, leadgeneratie systeem',
    h1: 'Lead Generatie — Automatisch meer gekwalificeerde leads',
    intro: 'Stop met wachten op klanten — ga ze actief benaderen. Wij bouwen complete lead generatie systemen die dagelijks kwalitatieve leads aanleveren voor uw salesteam. Voorspelbaar, schaalbaar en meetbaar.',
    problem: {
      title: 'Geen stabiele stroom van nieuwe klanten',
      points: [
        'Je bent afhankelijk van mond-tot-mondreclame',
        'Je salesteam heeft niet genoeg leads',
        'U weet niet hoeveel klanten uw volgende maand krijgt',
        'Je verliest potentiële klanten die niet direct kopen',
      ],
    },
    solution: {
      title: 'Een leadmachine die 24/7 voor u werkt',
      text: 'Wij bouwen een compleet systeem: van advertentie tot automatische opvolging. Bezoekers worden leads, leads worden klanten — alles geautomatiseerd en meetbaar.',
    },
    steps: [
      { n: 1, title: 'Strategie & Doelgroep', desc: 'We analyseren uw ideale klant en bepalen de beste kanalen om hen te bereiken.' },
      { n: 2, title: 'Funnel Bouwen', desc: 'Landingspagina, leadmagneet en formulieren die bezoekers omzetten naar leads.' },
      { n: 3, title: 'Traffic Genereren', desc: 'Google Ads, Meta Ads of SEO zorgen voor een constante stroom bezoekers.' },
      { n: 4, title: 'Automatisering', desc: 'E-mail funnels en CRM koppeling zorgen voor automatische opvolging.' },
    ],
    benefits: [
      'Voorspelbare stroom van nieuwe leads elke maand',
      'Minder afhankelijkheid van mond-tot-mondreclame',
      'Volledige automatisering van leadopvolging',
      'Schaalbaarheid — meer leads als u groeit',
      'Gekwalificeerde leads die klaar zijn om te kopen',
    ],
    services: [
      { name: 'Leadmachine Setup', price: '€1.250' },
      { name: 'Landingspagina voor Leads', price: '€650' },
      { name: 'E-mail Funnel Setup', price: '€650' },
      { name: 'Lead Generatie Abonnement', price: 'v.a. €750/mnd' },
    ],
  },
  {
    id: 'website-laten-maken',
    route: '/website-laten-maken',
    icon: Globe,
    title: 'Website Laten Maken',
    shortDesc: 'Professionele, snelle en conversiegerichte websites. Jouw online visitekaartje dat verkoopt.',
    metaTitle: 'Website Laten Maken — Professioneel & Conversiégericht | IMPERIA AI',
    metaDescription: 'Laat een professionele website maken door IMPERIA AI. SEO-geoptimaliseerd, mobiel, snel en conversiégericht. Starter v.a. €1.250.',
    keywords: 'website laten maken, webdesign bureau, website bouwen, professionele website',
    h1: 'Website laten maken — Professioneel, snel en conversiégericht',
    intro: 'Uw website is uw beste verkoper — en die is 24/7 beschikbaar. Wij bouwen websites die niet alleen mooi zijn, maar ook razendsnel laden, mobiel geoptimaliseerd zijn en bezoekers omzetten naar klanten.',
    problem: {
      title: 'Een website die niets oplevert',
      points: [
        'Uw website ziet er verouderd uit en wekt geen vertrouwen',
        'Bezoekers haken af voordat ze contact opnemen',
        'Uw website laadt traag op mobiel',
        'Je staat niet gevonden in Google',
      ],
    },
    solution: {
      title: 'Een website die voor u werkt',
      text: 'Wij ontwerpen en bouwen websites die zijn geoptimaliseerd voor conversie, snelheid en vindbaarheid. Van starter website tot premium maatwerk — altijd met SEO en mobiel als uitgangspunt.',
    },
    steps: [
      { n: 1, title: 'Intake & Strategie', desc: 'We bespreken uw doelen, doelgroep en wat de website moet opleveren.' },
      { n: 2, title: 'Design & Structuur', desc: 'Professioneel ontwerp dat past bij uw merk en conversiégericht is.' },
      { n: 3, title: 'Ontwikkeling', desc: 'Snelle, veilige website met SEO-fundament en mobiele optimalisatie.' },
      { n: 4, title: 'Lancering & Groei', desc: 'Website live, Google Search Console ingesteld en klaar voor verkeer.' },
    ],
    benefits: [
      'Professionele uitstraling die vertrouwen wekt',
      'Razendsnel — onder 2 seconden laadtijd',
      'SEO-geoptimaliseerd vanaf dag 1',
      'Volledig mobiel vriendelijk',
      'Conversiégericht ontwerp dat bezoekers omzet',
    ],
    services: [
      { name: 'Starter Website', price: '€1.250' },
      { name: 'Business Website', price: '€2.250' },
      { name: 'Premium Website', price: 'v.a. €3.750' },
      { name: 'Website Redesign', price: 'v.a. €1.500' },
      { name: 'Landingspagina', price: '€550' },
    ],
  },
  {
    id: 'social-media-advertising',
    route: '/social-media-advertising',
    icon: Share2,
    title: 'Social Media Advertising',
    shortDesc: 'Bereik uw doelgroep precies op Meta, TikTok en LinkedIn. Schaalbare social ads.',
    metaTitle: 'Social Media Advertising — Meta, TikTok & LinkedIn Ads | IMPERIA AI',
    metaDescription: 'Wij maken winstgevende social media advertenties op Meta, TikTok en LinkedIn. Van setup tot dagelijkse optimalisatie — meer bereik, meer leads.',
    keywords: 'social media advertising, meta ads bureau, facebook advertenties, tiktok ads, linkedin ads',
    h1: 'Social Media Advertising — Bereik uw doelgroep op elk platform',
    intro: 'Social media advertenties zijn de krachtigste manier om uw exacte doelgroep te bereiken — op basis van interesses, gedrag en demografische gegevens. Wij zetten winstgevende campagnes op die schaalbaar zijn en meetbare resultaten opleveren.',
    problem: {
      title: 'Social ads die geld kosten maar niets opleveren',
      points: [
        'Je betaalt voor bereik maar krijgt geen klanten',
        'U weet niet welke doelgroep echt converteert',
        'Je advertenties worden niet opgemerkt',
        'Je concurrenten groeien wel via social media',
      ],
    },
    solution: {
      title: 'Social ads die écht converteren',
      text: 'Met diepgaande doelgroepanalyse, professionele advertentiecreatieven en continue optimalisatie zorgen wij dat uw social ads niet alleen bereik genereren, maar ook echte klanten opleveren.',
    },
    steps: [
      { n: 1, title: 'Doelgroep Analyse', desc: 'We bepalen wie uw ideale klant is en hoe we hen het beste bereiken.' },
      { n: 2, title: 'Campagne Setup', desc: 'Professionele inrichting op Meta, TikTok of LinkedIn met pixel en tracking.' },
      { n: 3, title: 'Creatives & Copy', desc: 'Effectieve advertentie visuals en teksten die stoppen met scrollen.' },
      { n: 4, title: 'Optimalisatie', desc: 'Dagelijkse monitoring en wekelijkse optimalisaties voor steeds betere resultaten.' },
    ],
    benefits: [
      'Exact de juiste doelgroep bereiken',
      'Schaalbaar — verhoog budget als het werkt',
      'Volledige tracking en rapportage',
      'Snel resultaat zichtbaar binnen 2 weken',
      'Retargeting om verloren bezoekers terug te halen',
    ],
    services: [
      { name: 'Meta Ads Setup', price: '€650' },
      { name: 'TikTok Ads Setup', price: '€650' },
      { name: 'LinkedIn Ads Setup', price: '€750' },
      { name: 'Multichannel Ads Setup', price: '€1.250' },
      { name: 'Social Ads Beheer', price: 'v.a. €750/mnd' },
    ],
  },
  {
    id: 'marketing-strategie',
    route: '/marketing-strategie',
    icon: TrendingUp,
    title: 'Marketing Strategie',
    shortDesc: 'Van ambitie naar meetbare groei. Een duidelijke marketingstrategie op maat voor uw bedrijf.',
    metaTitle: 'Marketing Strategie op Maat — Van Ambitie naar Groei | IMPERIA AI',
    metaDescription: 'Wij ontwikkelen een complete marketingstrategie voor uw bedrijf. Data-gedreven, resultaatgericht en volledig afgestemd op uw doelen.',
    keywords: 'marketing strategie bureau, marketingplan, groeistrategie, marketing consultant',
    h1: 'Marketing Strategie op maat — Van ambitie naar meetbare groei',
    intro: 'Groeien zonder strategie is gokken. Wij ontwikkelen een complete, data-gedreven marketingstrategie die precies aangeeft welke kanalen, boodschappen en acties u het snelst naar uw doelen brengen.',
    problem: {
      title: 'Geen richting, geen consistentie, geen groei',
      points: [
        'Je doet van alles maar ziet geen samenhang',
        'U weet niet welke kanalen het meeste opleveren',
        'Je marketingbudget wordt niet optimaal ingezet',
        'Je concurrenten groeien sneller dan jij',
      ],
    },
    solution: {
      title: 'Een strategie die werkt — onderbouwd met data',
      text: 'Wij analyseren uw markt, doelgroep en concurrenten en ontwikkelen een duidelijk marketingplan met concrete acties, KPIs en een tijdlijn. Zo weet u precies wat u wanneer moet doen.',
    },
    steps: [
      { n: 1, title: 'Situatie Analyse', desc: 'Markt, concurrentie, doelgroep en huidige marketing doorgelicht.' },
      { n: 2, title: 'Strategie Ontwikkeling', desc: 'Duidelijke positionering, kanaalstrategie en boodschappen.' },
      { n: 3, title: 'Actieplan', desc: 'Concrete taken, verantwoordelijkheden en tijdlijn.' },
      { n: 4, title: 'Implementatie & Meten', desc: 'Uitvoering met maandelijkse bijsturing op basis van resultaten.' },
    ],
    benefits: [
      'Duidelijke richting voor alle marketingactiviteiten',
      'Budget wordt optimaal ingezet op de juiste kanalen',
      'Meetbare KPIs en doelstellingen',
      'Concurrentievoordeel door scherpe positionering',
      'Snellere groei door focus en consistentie',
    ],
    services: [
      { name: 'Concurrentie & Marktanalyse', price: '€299' },
      { name: 'Marketing Strategie Sessie', price: 'op aanvraag' },
      { name: 'Full-service Marketing', price: 'v.a. €1.500/mnd' },
    ],
  },
  {
    id: 'funnels',
    route: '/funnels',
    icon: Layers,
    title: 'Funnels & Leadmachine',
    shortDesc: 'Complete sales funnels die automatisch leads genereren en opwarmen tot koopklare klanten.',
    metaTitle: 'Funnel Bouwen & Leadmachine — Automatisch Meer Omzet | IMPERIA AI',
    metaDescription: 'Wij bouwen complete sales funnels en leadmachines die automatisch leads genereren, opwarmen en omzetten naar betalende klanten.',
    keywords: 'funnel bouwen, sales funnel, leadmachine, marketing funnel, funnel bureau',
    h1: 'Funnels & Leadmachine — Automatisch leads genereren en omzetten',
    intro: 'Een goed gebouwde funnel werkt 24/7 voor u — ook als u slaapt. Wij bouwen complete sales funnels die bezoekers stap voor stap begeleiden van eerste interesse naar betalende klant. Volledig geautomatiseerd.',
    problem: {
      title: 'Leads die u kwijtraakt voordat ze kopen',
      points: [
        'Bezoekers verlaten uw website zonder contact op te nemen',
        'Leads die u genereert worden niet goed opgevolgd',
        'Je mist omzet van mensen die wel interesse hadden',
        'Je salesproces is te afhankelijk van handmatige opvolging',
      ],
    },
    solution: {
      title: 'Een funnel die automatisch converteert',
      text: 'Van de eerste advertentie tot de betaling — wij bouwen een complete funnel die elke stap automatiseert. Bezoekers worden leads, leads worden klanten, klanten worden terugkerende kopers.',
    },
    steps: [
      { n: 1, title: 'Funnel Strategie', desc: 'We bepalen de optimale route van bezoeker naar klant voor uw product.' },
      { n: 2, title: 'Landingspagina & Lead Magneet', desc: 'Converterende pagina\'s en een waardevolle aanbieding die contactgegevens oplevert.' },
      { n: 3, title: 'E-mail Automatisering', desc: 'Geautomatiseerde e-mailreeksen die leads opwarmen en aanzetten tot aankoop.' },
      { n: 4, title: 'Optimalisatie & Schaling', desc: 'Continue verbetering op basis van data voor steeds hogere conversieratio\'s.' },
    ],
    benefits: [
      'Automatische leadgeneratie zonder handmatige inspanning',
      'Hogere conversieratio van bezoeker naar klant',
      'Minder verloren leads door geautomatiseerde opvolging',
      'Schaalbaar — meer resultaat met hetzelfde budget',
      'Meetbaar — u ziet precies waar mensen afhaken',
    ],
    services: [
      { name: 'Leadmachine Setup', price: '€1.250' },
      { name: 'Webinar Funnel', price: '€1.500' },
      { name: 'E-mail Funnel Setup', price: '€650' },
      { name: 'Landingspagina', price: '€650' },
    ],
  },
  {
    id: 'advertising-setup',
    route: '/advertising-setup',
    icon: Settings,
    title: 'Advertising Setup',
    shortDesc: 'Professionele inrichting van advertentiecampagnes op elk platform. Direct resultaat.',
    metaTitle: 'Advertising Setup — Professionele Campagne Inrichting | IMPERIA AI',
    metaDescription: 'Wij richten uw advertentiecampagnes professioneel in op Google, Meta, TikTok en LinkedIn. Van setup tot live in 7 dagen.',
    keywords: 'advertising setup, advertenties instellen, google ads setup, meta ads setup, campagne inrichting',
    h1: 'Advertising Setup — Professionele campagnes die direct converteren',
    intro: 'Een goed ingerichte advertentiecampagne is het fundament van succesvolle online marketing. Wij richten uw campagnes professioneel in — met de juiste structuur, targeting, tracking en biedstrategie — zodat u budget optimaal wordt benut.',
    problem: {
      title: 'Slecht ingerichte campagnes kosten u geld',
      points: [
        'Je advertenties bereiken de verkeerde mensen',
        'Je conversietracking is niet correct ingesteld',
        'Je betaalt te veel per klik door slechte biedstrategie',
        'U weet niet of uw campagnes écht werken',
      ],
    },
    solution: {
      title: 'Een professionele setup die resultaat oplevert',
      text: 'Wij richten uw campagnes in zoals de experts dat doen — met bewezen structuren, correcte tracking en slimme targetingstrategieën. Binnen 7 werkdagen bent u live met campagnes die converteren.',
    },
    steps: [
      { n: 1, title: 'Analyse & Research', desc: 'Zoekwoordenonderzoek, doelgroepanalyse en concurrentiescan.' },
      { n: 2, title: 'Account Setup', desc: 'Correcte accountstructuur, campagnes, advertentiegroepen en advertenties.' },
      { n: 3, title: 'Tracking Implementatie', desc: 'Conversietracking, pixel installatie en analytics koppeling.' },
      { n: 4, title: 'Live & Optimaliseren', desc: 'Campagne live, eerste data verzamelen en direct bijsturen.' },
    ],
    benefits: [
      'Campagne live binnen 7 werkdagen',
      'Correcte tracking van elke conversie',
      'Professionele structuur die schaalbaar is',
      'Lagere kosten per klik door slimme biedstrategieën',
      'Transparante rapportage over resultaten',
    ],
    services: [
      { name: 'Google Ads Setup', price: '€650' },
      { name: 'Meta Ads Setup', price: '€650' },
      { name: 'TikTok Ads Setup', price: '€650' },
      { name: 'LinkedIn Ads Setup', price: '€750' },
      { name: 'Multichannel Setup', price: '€1.250' },
    ],
  },
  {
    id: 'content-marketing',
    route: '/content-marketing',
    icon: Video,
    title: 'Content Marketing',
    shortDesc: 'Bedrijfsvideo\'s, reels, social content en advertenties die uw merk laten groeien.',
    metaTitle: 'Content Marketing & Bedrijfsvideo\'s — IMPERIA AI',
    metaDescription: 'IMPERIA AI maakt bedrijfsvideo\'s, promotievideo\'s, reels en social content die uw merk zichtbaar maken en converteren.',
    keywords: 'content marketing, bedrijfsvideo, promotievideo, reels, social content, video marketing',
    h1: 'Content Marketing — Bedrijfsvideo\'s die uw merk laten groeien',
    intro: 'Goede content is wat klanten van u doet kopen. Wij produceren professionele bedrijfsvideo\'s, promotievideo\'s, reels en social content die uw merk versterken én converteren. Van scriptidee tot publicatie — alles uit één hand.',
    problem: {
      title: 'Uw merk valt online niet op',
      points: [
        'U heeft geen tijd om regelmatig content te maken',
        'Uw social posts krijgen weinig bereik en interactie',
        'U mist video\'s die uw bedrijf goed presenteren',
        'Adverteren werkt niet zonder sterke creatives',
      ],
    },
    solution: {
      title: 'Wij maken content waar uw doelgroep op reageert',
      text: 'Onze contentstrategen en videografen produceren content die past bij uw merk, doelgroep en doelen. Bedrijfsvideo\'s voor vertrouwen, reels voor bereik, advertenties die converteren. Inclusief planning en publicatie.',
    },
    steps: [
      { n: 1, title: 'Contentstrategie', desc: 'We bepalen samen welke content uw merk vooruit helpt.' },
      { n: 2, title: 'Productie', desc: 'Opname op locatie, scripting, editing en grafische ondersteuning.' },
      { n: 3, title: 'Publicatie & Planning', desc: 'Content wordt gepland en gepubliceerd op de juiste kanalen.' },
      { n: 4, title: 'Optimalisatie', desc: 'We meten wat werkt en sturen bij voor maximale impact.' },
    ],
    benefits: [
      'Professionele bedrijfsvideo\'s die vertrouwen opbouwen',
      'Reels en social content met hoog bereik',
      'Sterke advertentie creatives die converteren',
      'Consistent online aanwezig — zonder zelf te hoeven posten',
      'Volledige planning en uitvoering uit handen',
    ],
    services: [
      { name: 'Bedrijfsvideo', price: 'v.a. €1.450' },
      { name: 'Promotievideo', price: 'v.a. €950' },
      { name: 'Social Content Pakket', price: 'v.a. €650/mnd' },
      { name: 'Reels Productie', price: 'v.a. €450' },
      { name: 'Advertentie Creatives', price: 'v.a. €350' },
    ],
  },
  {
    id: 'strategie-en-groei',
    route: '/strategie-en-groei',
    icon: Compass,
    title: 'Strategie & Groei',
    shortDesc: 'Marketingstrategie, groeiplan en uitvoering — van plan tot resultaat.',
    metaTitle: 'Marketing Strategie & Groei | IMPERIA AI',
    metaDescription: 'Een sterke marketingstrategie is de basis voor duurzame groei. Wij maken het plan en voeren het uit voor MKB-bedrijven.',
    keywords: 'marketingstrategie, groeistrategie, marketingplan, strategisch advies, groeiplan',
    h1: 'Strategie & Groei — Een marketingplan dat ook écht wordt uitgevoerd',
    intro: 'De meeste bedrijven hebben geen gebrek aan marketing — maar wel aan een strategie die alles verbindt. Wij maken een concreet groeiplan op basis van uw situatie, doelen en doelgroep, en voeren het samen met u uit. Geen mooie rapporten, wel resultaat.',
    problem: {
      title: 'Marketing zonder plan kost veel en levert weinig op',
      points: [
        'U doet veel, maar het lijkt geen samenhang te hebben',
        'U weet niet welk kanaal de meeste klanten oplevert',
        'Uw budget gaat naar dingen waarvan u het effect niet kent',
        'U mist iemand die meedenkt op strategisch niveau',
      ],
    },
    solution: {
      title: 'Een groeiplan dat u stap voor stap uitvoert',
      text: 'Wij maken een marketingstrategie die past bij uw bedrijf, markt en ambitie. Niet 60 pagina\'s theorie, maar een werkbaar plan met prioriteiten, KPI\'s en een tijdlijn. En we helpen bij de uitvoering — als strategisch sparringpartner of als verlengstuk van uw team.',
    },
    steps: [
      { n: 1, title: 'Intake & Analyse', desc: 'Wij brengen uw bedrijf, markt, concurrenten en doelgroep in kaart.' },
      { n: 2, title: 'Strategie & Plan', desc: 'Concreet plan met kanalen, doelen, KPI\'s en investeringen.' },
      { n: 3, title: 'Uitvoering', desc: 'Wij coördineren of voeren de marketing uit — afhankelijk van uw wensen.' },
      { n: 4, title: 'Sturing & Groei', desc: 'Maandelijks evalueren we resultaten en sturen we bij.' },
    ],
    benefits: [
      'Een marketingstrategie die past bij uw bedrijf en budget',
      'Duidelijke prioriteiten — geen wildgroei aan losse acties',
      'Vast aanspreekpunt op strategisch niveau',
      'Meetbare KPI\'s zodat u groei kunt sturen',
      'Volledige executiekracht — wij doen het ook',
    ],
    services: [
      { name: 'Strategiesessie', price: '€650' },
      { name: 'Groeiplan (uitgewerkt)', price: '€1.250' },
      { name: 'Marketing Strateeg Abonnement', price: 'v.a. €950/mnd' },
      { name: 'Concurrentie Analyse', price: '€550' },
    ],
  },
];

export const getDienstByRoute = (route) => DIENSTEN.find(d => d.route === route);
